import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * tailwind-merge has to be taught this design system's font-size scale.
 *
 * Its default scale is `xs · sm · base · lg · …`. Ours adds `base-sm`, `md`,
 * `doc-title` and friends (tokens.md §3), and a size it does not recognise is
 * guessed to be a text COLOUR — which puts it in the same conflict group as
 * `text-info-700`, so the last one written silently wins and the size is
 * dropped.
 *
 * That was not hypothetical: `InfoBox` composed `text-base-sm` with its tone's
 * `text-{tone}-700` and shipped without a font size at all, at every one of its
 * call sites. Nothing caught it because both classes are present in the source
 * and only the merged output is wrong.
 */

/**
 * The non-numeric keys `tailwind.preset.ts` adds to Tailwind's spacing scale.
 * tailwind-merge classifies `h-control-md` as a height ONLY if it knows the
 * value; otherwise the class is unknown, never conflicts, and a caller's
 * `h-11` cannot override a variant's token — the stylesheet order decides
 * (BL-103, IMM-785). Keep this in step with the preset: the test in
 * apps/web-app/src/__tests__/ui-kit-cn-token-scales.test.ts asserts this list
 * equals the one derived from the preset, so a key added to either alone
 * fails.
 */
export const SPACING_TOKEN_KEYS = [
  'sidebar',
  'detail-panel',
  'topbar',
  'toolbar',
  'table-header',
  'table-row',
  'sidebar-item',
  'control-sm',
  'control-md',
  'control-lg',
  'spinner-sm',
  'spinner-md',
  'spinner-lg',
] as const;

const twMerge = extendTailwindMerge({
  extend: {
    theme: { spacing: [...SPACING_TOKEN_KEYS] },
    classGroups: {
      'font-size': [{ text: ['xs', 'sm', 'base-sm', 'base', 'md', 'lg', 'xl', '2xl', '3xl', '4xl', 'doc-title'] }],
    },
  },
});

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}
